<script>
	import { median } from 'd3-array';

	import Grid from './components/Grid.svelte';
	import Axes from './components/Axes.svelte';
	import QuadrantLines from './components/QuadrantLines.svelte';
	import QuadrantLabels from './components/QuadrantLabels.svelte';
	import ScatterLayer from './components/ScatterLayer.svelte';
	import MunicipalityTrail from './components/MunicipalityTrail.svelte';
	import Legend from './components/Legend.svelte';
	import Controls from './components/Controls.svelte';
	import Tooltip from './components/Tooltip.svelte';

	import { prepareAll, getTrajectory } from './utils/prepareData.js';
	import { buildScales, setRange } from './utils/scales.js';
	import { buildPartyHelpers } from './utils/parties.js';
	import { isMobileViewport, plotSize, debounce } from './utils/responsive.js';
	import { observeVisibility } from './utils/intersection.js';
	import { createAutoplayController } from './utils/autoplayYears.js';
	import {
		AUTOPLAY_STEP_MS,
		END_YEAR_PAUSE_MS,
		INTERSECTION_ROOT_MARGIN,
		INTERSECTION_THRESHOLD
	} from './utils/constants.js';

	/**
	 * Root component. Owns all global state: data, year, muted parties,
	 * pinned muni, hover tooltip. Distributes derived values via props.
	 *
	 * Kept as a coordinator — heavy logic lives in src/lib/utils/.
	 */
	let { dataUrl, namesUrl = null, initialYear = null, parties: partiesOverride = null } = $props();

	// ---- Async data loading -------------------------------------------------
	let raw = $state(null);
	let names = $state({});
	let loadError = $state(null);

	$effect(() => {
		let cancelled = false;
		const dataReq = fetch(dataUrl).then((r) => {
			if (!r.ok) throw new Error('No se pudo cargar ' + dataUrl);
			return r.json();
		});
		const namesReq = namesUrl
			? fetch(namesUrl)
					.then((r) => (r.ok ? r.json() : {}))
					.catch(() => ({}))
			: Promise.resolve({});

		Promise.all([dataReq, namesReq])
			.then(([R, N]) => {
				if (cancelled) return;
				raw = R;
				names = N || {};
			})
			.catch((err) => {
				if (cancelled) return;
				loadError = err.message || 'Error de carga';
			});
		return () => {
			cancelled = true;
		};
	});

	// ---- Derived: prepared data, years --------------------------------------
	const prepared = $derived(raw ? prepareAll(raw, names) : null);
	const years = $derived(prepared?.years ?? []);

	// Domains depend on the full dataset, so compute them once when data
	// changes. We keep this separate from the ranged `scales` derived below
	// so we don't walk 9.5k records on every resize.
	const scaleDomains = $derived(prepared ? buildScales(prepared) : null);

	const { partyColor, partyLongName } = buildPartyHelpers(partiesOverride || {});

	// ---- Year + filters + pin ----------------------------------------------
	let year = $state(null);
	let mutedParties = $state(new Set());
	let pinned = $state(null);

	// Pick the initial year once data is in.
	$effect(() => {
		if (!prepared) return;
		if (year != null) return;
		if (initialYear && prepared.years.includes(initialYear)) year = initialYear;
		else year = prepared.years[prepared.years.length - 1];
	});

	const yearData = $derived(year && prepared ? prepared.byYear[year] : []);

	// Parties present this year, sorted by frequency.
	const partiesThisYear = $derived.by(() => {
		const counts = {};
		for (const d of yearData) counts[d.winner] = (counts[d.winner] || 0) + 1;
		return Object.keys(counts)
			.sort((a, b) => counts[b] - counts[a])
			.map((name) => {
				const sample = yearData.find((r) => r.winner === name);
				return { name, code: sample?.winnerCode || null };
			});
	});

	const visibleData = $derived(yearData.filter((d) => !mutedParties.has(d.winner)));

	const medianX = $derived(median(visibleData, (d) => d.participacion_pct) || 0);
	const medianY = $derived(median(visibleData, (d) => d.margen_pct) || 0);

	const trajectory = $derived(pinned && prepared ? getTrajectory(prepared, pinned) : []);

	// ---- Container + responsive --------------------------------------------
	// `width` starts at 0 so the chart never renders with a placeholder size
	// (which would produce a one-frame layout flash with squished ticks).
	let containerEl = $state();
	let width = $state(0);
	let isMobile = $state(false);

	$effect(() => {
		if (!containerEl) return;
		const measure = () => {
			width = containerEl.clientWidth || 0;
			isMobile = isMobileViewport();
		};
		measure();
		const ro = new ResizeObserver(debounce(measure, 80));
		ro.observe(containerEl);
		const mql = window.matchMedia('(max-width: 599px)');
		const onMq = () => (isMobile = mql.matches);
		mql.addEventListener('change', onMq);
		return () => {
			ro.disconnect();
			mql.removeEventListener('change', onMq);
		};
	});

	const layout = $derived(plotSize(width || 1, isMobile));

	/**
	 * Ranged scales. Clones the cached `scaleDomains` so a new reference is
	 * produced whenever the container resizes — this propagates the range
	 * change reactively to every child consuming `scales.x`, `scales.y` etc.
	 * Returns null until we have valid dimensions AND data, which the
	 * `isReady` guard below uses to gate the SVG render.
	 */
	const scales = $derived.by(() => {
		if (!scaleDomains || width === 0) return null;
		const s = {
			x: scaleDomains.x.copy(),
			y: scaleDomains.y.copy(),
			r: scaleDomains.r
		};
		setRange(s, layout.innerW, layout.innerH);
		return s;
	});

	const isReady = $derived(prepared != null && year != null && scales != null && layout.innerW > 0);

	// ---- Autoplay (scroll-driven) ------------------------------------------
	// Respect users with prefers-reduced-motion: reduce. Captured once at
	// mount; live changes would require an extra matchMedia listener which
	// isn't editorially impactful for this chart.
	const reducedMotion =
		typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	/** Whether the chart is "near or visible" per IntersectionObserver. */
	let visible = $state(false);

	/**
	 * User-controlled toggle. Default ON unless the user asked for reduced
	 * motion. The play button in <Controls> only flips this; whether the
	 * controller is *actually* ticking is the AND of (playing && visible).
	 */
	let playing = $state(!reducedMotion);

	const autoplay = createAutoplayController({
		step: AUTOPLAY_STEP_MS,
		endPause: END_YEAR_PAUSE_MS
	});

	// Refresh the controller's view of years / setter whenever data
	// reloads. Reads through getters so the controller always sees the
	// current Svelte state without us re-creating it.
	$effect(() => {
		autoplay.setRefs(
			years,
			() => year,
			(y) => {
				year = y;
			}
		);
	});

	// Single source of truth: actually ticking = playing AND visible.
	$effect(() => {
		if (playing && visible && years.length > 0) autoplay.start();
		else autoplay.stop();
	});

	// Always stop on unmount — covers SPA navigation away.
	$effect(() => () => autoplay.destroy());

	function handleVisibility(v) {
		visible = v;
	}

	function togglePlay() {
		playing = !playing;
	}

	function handleManualYearChange(y) {
		year = y;
		// User dragged the slider: reset cadence and cancel any pending
		// end-of-loop pause so they're not surprised by a jump.
		autoplay.notifyUserChange();
	}

	// ---- Tooltip state ------------------------------------------------------
	/** { record, x, y } in container coordinates; null when nothing hovered. */
	let hover = $state(null);

	function handleHover(record, evt) {
		if (!containerEl) return;
		const rect = containerEl.getBoundingClientRect();
		hover = {
			record,
			x: evt.clientX - rect.left,
			y: evt.clientY - rect.top
		};
	}
	function handleLeave() {
		if (pinned == null) hover = null;
	}
	function handleClick(record) {
		if (pinned === record.munCod) {
			pinned = null;
			hover = null;
			return;
		}
		pinned = record.munCod;
		// Keep the tooltip pinned on the clicked record.
		hover = { record, x: hover?.x ?? 0, y: hover?.y ?? 0 };
	}
	function clearPin() {
		pinned = null;
		hover = null;
	}

	function toggleParty(name) {
		const next = new Set(mutedParties);
		if (next.has(name)) next.delete(name);
		else next.add(name);
		mutedParties = next;
	}

	// Dismiss pinned tooltip on outside click / Esc.
	$effect(() => {
		function onDoc(e) {
			if (pinned == null) return;
			if (e.target.closest('.tooltip')) return;
			if (e.target.closest('circle.dot')) return;
			clearPin();
		}
		function onKey(e) {
			if (e.key === 'Escape') clearPin();
		}
		document.addEventListener('click', onDoc);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('click', onDoc);
			document.removeEventListener('keydown', onKey);
		};
	});
</script>

<div
	class="ep-scatter ep-grafico"
	bind:this={containerEl}
	use:observeVisibility={{
		onChange: handleVisibility,
		rootMargin: INTERSECTION_ROOT_MARGIN,
		threshold: INTERSECTION_THRESHOLD
	}}
>
	{#if loadError}
		<div class="error">No se pudieron cargar los datos. {loadError}</div>
	{:else if !isReady}
		<!--
      Keep this branch lightweight: the only DOM we need before isReady
      is the container itself (already mounted via bind), so the
      ResizeObserver can give us a real width. No SVG, no scales — that
      avoids the "squished axes" frame on first paint.
    -->
		<div class="loading">Cargando datos…</div>
	{:else}
		<Controls
			{years}
			{year}
			{playing}
			{pinned}
			onYearChange={handleManualYearChange}
			onTogglePlay={togglePlay}
			onClearPin={clearPin}
		/>
		<Legend
			parties={partiesThisYear}
			{mutedParties}
			{partyColor}
			{partyLongName}
			onToggle={toggleParty}
		/>
		<div class="chart-wrap">
			<svg
				viewBox="0 0 {layout.width} {layout.height}"
				width={layout.width}
				height={layout.height}
				role="img"
				aria-label="Scatterplot con participación electoral y margen del ganador."
			>
				<title>Participación vs margen del ganador por municipio.</title>
				<desc>
					Cada círculo es un municipio andaluz. Eje X: participación. Eje Y: ventaja del ganador
					sobre el segundo. Tamaño: censo electoral.
				</desc>
				<g class="plot" transform="translate({layout.margin.left},{layout.margin.top})">
					<Grid yScale={scales.y} innerW={layout.innerW} />
					<Axes xScale={scales.x} yScale={scales.y} innerW={layout.innerW} innerH={layout.innerH} />

					<ScatterLayer
						data={visibleData}
						xScale={scales.x}
						yScale={scales.y}
						rScale={scales.r}
						pinnedCod={pinned}
						{partyColor}
						onHover={handleHover}
						onLeave={handleLeave}
						onClick={handleClick}
					/>
					<MunicipalityTrail points={trajectory} xScale={scales.x} yScale={scales.y} />
					<QuadrantLines
						xScale={scales.x}
						yScale={scales.y}
						innerW={layout.innerW}
						innerH={layout.innerH}
						{medianX}
						{medianY}
					/>
					<QuadrantLabels innerW={layout.innerW} innerH={layout.innerH} {isMobile} />
				</g>
			</svg>
		</div>

		<Tooltip
			record={hover?.record ?? null}
			x={hover?.x ?? 0}
			y={hover?.y ?? 0}
			{isMobile}
			pinned={pinned != null}
			{partyColor}
			{partyLongName}
			onClose={clearPin}
		/>
	{/if}
</div>
